import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/logo/defra.png";
import { Link } from "react-router-dom";

const NAV_ITEMS = [
  { label: "Tentang", to: "/#AboutUs" },
  { label: "Layanan", to: "/#OurServices" },
  { label: "Kelebihan", to: "/#OurAdvantages" },
  { label: "Project", to: "/#ProjectsPage" },
  { label: "Klien", to: "/#ClientsPage" },
];

export default function Header2() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open (iOS Safari compatible)
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [mobileMenuOpen]);

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 w-full z-40 transition-all duration-500 ${
          scrolled
            ? "glass shadow-soft py-1"
            : "bg-white/80 backdrop-blur-md border-b border-slate-100 py-2"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <motion.img
              whileHover={{ rotate: -5, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
              src={logo}
              alt="Defra Logo"
              className={`transition-all duration-500 ${
                scrolled ? "h-12" : "h-14"
              } w-auto`}
            />
            <span className="hidden sm:block font-oswald-header text-lg lg:text-xl text-slate-900">
              DEFRA WATER
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="text-sm font-semibold tracking-wide text-slate-800 hover:text-brand-600 transition-colors no-underline hover:no-underline link-underline"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/blog"
              className="text-sm font-semibold tracking-wide text-brand-600 transition-colors no-underline hover:no-underline link-underline"
            >
              Blog
            </Link>
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm bg-gradient-to-r from-brand-600 to-eco-600 text-white shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5"
            >
              Log in
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center rounded-md p-2.5"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open menu</span>
            <svg
              className="h-7 w-7 text-slate-900"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </button>
        </nav>
      </motion.header>

      {/* Mobile Menu (rendered outside header) */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-[60]">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setMobileMenuOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="absolute right-0 top-0 h-full w-full sm:w-80 bg-white shadow-2xl flex flex-col overflow-y-auto"
              style={{ backgroundColor: "#ffffff" }}
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-100">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 no-underline hover:no-underline"
                >
                  <img src={logo} alt="Defra" className="h-12 w-auto" />
                  <span className="font-oswald-header text-base text-slate-900">
                    DEFRA
                  </span>
                </Link>
                <button
                  type="button"
                  className="rounded-full p-2 hover:bg-slate-100 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <svg
                    className="h-6 w-6 text-slate-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="flex-1 flex flex-col gap-1 p-4 overflow-y-auto">
                {NAV_ITEMS.map((item, idx) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 rounded-xl text-base font-semibold text-slate-800 hover:bg-gradient-to-r hover:from-brand-50 hover:to-eco-50 hover:text-brand-700 transition-all no-underline hover:no-underline"
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ))}
                <Link
                  to="/blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 rounded-xl text-base font-semibold text-slate-800 hover:bg-gradient-to-r hover:from-brand-50 hover:to-eco-50 hover:text-brand-700 transition-all no-underline hover:no-underline"
                >
                  Blog
                </Link>
              </div>

              <div className="p-4 border-t border-slate-100">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold shadow-lg shadow-brand-500/30 no-underline hover:no-underline"
                >
                  Log in
                  <span>→</span>
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
